import { useOutletContext, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Bone, Cpu, Sparkles, Footprints, Plane, ScanLine, Check } from "lucide-react";
import { Container, Reveal, Eyebrow, SectionHeading, FAQ, siteImg } from "@/site/ui";
import CallbackForm from "@/site/CallbackForm";
import TestimonialSlider from "@/site/TestimonialSlider";

const SERVICE_ICONS = [Bone, Cpu, Sparkles, Footprints, Plane, ScanLine];

export default function Home() {
  const c = useOutletContext();

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative pt-52 pb-16 md:pt-60 md:pb-24 overflow-hidden">
        <Container className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          <div className="lg:col-span-6">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
              <Eyebrow>{c.hero.eyebrow}</Eyebrow>
              <h1 className="mt-5 text-5xl sm:text-6xl md:text-7xl font-bold tracking-[-0.03em] leading-[0.98]">
                {c.hero.title}
              </h1>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-[#525252]">{c.hero.subtitle}</p>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <a href="#callback" data-testid="hero-cta-primary"
                  className="inline-flex items-center gap-2 rounded-full bg-[var(--rgh-primary)] px-7 py-3.5 text-sm font-semibold text-white transition-[transform,background-color,box-shadow] duration-200 hover:bg-[var(--rgh-primary-dark)] hover:-translate-y-0.5 hover:shadow-xl">
                  {c.hero.cta_primary} <ArrowRight size={16} />
                </a>
                <Link to="/services" data-testid="hero-cta-secondary"
                  className="inline-flex items-center gap-2 rounded-full border border-black/10 px-7 py-3.5 text-sm font-semibold text-[#0A0A0A] transition-colors duration-200 hover:border-[var(--rgh-primary)] hover:text-[var(--rgh-primary)]">
                  {c.hero.cta_secondary}
                </Link>
              </div>
            </motion.div>
          </div>
          <div className="lg:col-span-6">
            <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="relative">
              <img src={siteImg(c.hero.image)} alt="Prosthetic and orthotic care" className="w-full h-[420px] md:h-[520px] object-cover rounded-[28px] shadow-2xl" />
              <div className="absolute -bottom-6 -left-2 sm:left-6 bg-white rounded-2xl shadow-xl px-6 py-4 border border-black/5">
                <p className="text-3xl font-bold text-[var(--rgh-primary)]">5,000+</p>
                <p className="text-xs text-[#525252] mt-0.5">lives helped to live better</p>
              </div>
            </motion.div>
          </div>
        </Container>
      </section>

      {/* HIGHLIGHTS */}
      <section className="py-16 md:py-24">
        <Container>
          <Reveal><SectionHeading eyebrow="What we do" title="Precision care, beautifully engineered" sub="Three pillars of mobility and restoration, each crafted around you." /></Reveal>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {c.highlights.map((h, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <article data-testid={`highlight-${i}`} className="group h-full rounded-3xl overflow-hidden bg-white border border-gray-100 shadow-sm transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 hover:shadow-xl">
                  <div className="h-56 overflow-hidden">
                    <img src={siteImg(h.image)} alt={h.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="p-7">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--rgh-primary)]">{h.subtitle}</p>
                    <h3 className="mt-2 text-xl font-bold tracking-tight">{h.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-[#525252]">{h.desc}</p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* WELCOME */}
      <section className="py-16 md:py-24 bg-[#FAFAFA]">
        <Container className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal>
            <img src={siteImg(c.welcome.image)} alt="Raphadon clinicians" className="w-full h-[440px] object-cover rounded-[28px] shadow-lg" />
          </Reveal>
          <Reveal delay={0.1}>
            <Eyebrow>Welcome</Eyebrow>
            <h2 className="mt-4 text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">{c.welcome.heading}</h2>
            <p className="mt-5 text-base sm:text-lg leading-relaxed text-[#525252]">{c.welcome.body}</p>
            <div className="mt-8 grid grid-cols-3 gap-6">
              {(c.welcome.stats || []).map((s, i) => (
                <div key={i}>
                  <p className="text-3xl font-bold text-[var(--rgh-primary)]">{s.value}</p>
                  <p className="text-xs text-[#525252] mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </Container>
      </section>

      {/* SERVICES */}
      <section className="py-16 md:py-24">
        <Container>
          <Reveal><SectionHeading center eyebrow="Our services" title="A complete spectrum of mobility care" /></Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {c.services.map((s, i) => {
              const Icon = SERVICE_ICONS[i % SERVICE_ICONS.length];
              return (
                <Reveal key={i} delay={(i % 3) * 0.08}>
                  <article data-testid={`service-${i}`} className="group h-full rounded-2xl border border-gray-100 bg-white p-8 shadow-sm transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-lg">
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[rgb(var(--rgh-primary-rgb)/0.08)] text-[var(--rgh-primary)] transition-colors duration-300 group-hover:bg-[var(--rgh-primary)] group-hover:text-white">
                      <Icon size={22} />
                    </span>
                    <h3 className="mt-5 text-lg font-bold tracking-tight">{s.title}</h3>
                    <p className="mt-2.5 text-sm leading-relaxed text-[#525252]">{s.desc}</p>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </Container>
      </section>

      {/* WHY CHOOSE */}
      <section className="py-16 md:py-24 bg-[#FAFAFA]">
        <Container>
          <Reveal><SectionHeading eyebrow="Why Raphadon" title={c.why_choose.heading} sub={c.why_choose.subtitle} /></Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {c.why_choose.points.map((p, i) => (
              <Reveal key={i} delay={(i % 4) * 0.07}>
                <div data-testid={`why-${i}`} className="h-full rounded-2xl bg-white border border-gray-100 p-7 shadow-sm">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[var(--rgh-primary)] text-white text-sm font-bold">{i + 1}</span>
                  <h3 className="mt-5 text-lg font-bold tracking-tight">{p.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-[#525252]">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* PROCESS */}
      <section className="py-16 md:py-24">
        <Container>
          <Reveal><SectionHeading center eyebrow="Our process" title={c.process.heading} sub={c.process.subtitle} /></Reveal>
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-10 relative">
            {c.process.steps.map((s, i) => (
              <Reveal key={i} delay={(i % 4) * 0.08}>
                <div data-testid={`process-${i}`} className="relative">
                  <div className="flex items-center gap-3">
                    <span className="text-4xl font-bold text-[rgb(var(--rgh-primary-rgb)/0.20)]">{String(i + 1).padStart(2, "0")}</span>
                    <span className="h-px flex-1 bg-black/10" />
                  </div>
                  <h3 className="mt-4 text-lg font-bold tracking-tight">{s.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-[#525252]">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-16 md:py-24 bg-[#0A0A0A]">
        <Container className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-4">
            <Reveal><SectionHeading dark eyebrow="Testimonials" title="Stories of living better" /></Reveal>
          </div>
          <div className="lg:col-span-8">
            <Reveal delay={0.1}><TestimonialSlider items={c.testimonials} dark /></Reveal>
          </div>
        </Container>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24">
        <Container className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4"><Reveal><SectionHeading eyebrow="FAQ" title="Questions, answered" /></Reveal></div>
          <div className="lg:col-span-8"><Reveal delay={0.1}><FAQ items={c.faq} /></Reveal></div>
        </Container>
      </section>

      {/* CTA BANNER */}
      <section className="pb-4">
        <Container>
          <Reveal>
            <div className="relative overflow-hidden rounded-[32px] bg-[var(--rgh-primary)] px-8 py-14 sm:px-12 md:px-16 md:py-20 text-center">
              <div className="absolute -top-16 -right-16 h-56 w-56 rounded-full bg-white/10" />
              <div className="absolute -bottom-20 -left-10 h-64 w-64 rounded-full bg-black/10" />
              <div className="relative">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white">{c.cta.heading}</h2>
                <p className="mt-4 max-w-2xl mx-auto text-base sm:text-lg text-white/85">{c.cta.subtitle}</p>
                <a href="#callback" data-testid="cta-banner-button" className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 text-sm font-semibold text-[var(--rgh-primary)] transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-xl">
                  {c.cta.button} <ArrowRight size={16} />
                </a>
              </div>
            </div>
          </Reveal>
        </Container>
      </section>

      {/* CALLBACK */}
      <section id="callback" className="py-16 md:py-24 scroll-mt-24">
        <Container>
          <Reveal>
            <div className="rounded-[32px] bg-[#FAFAFA] border border-gray-100 p-8 sm:p-12 md:p-16">
              <div className="max-w-2xl">
                <Eyebrow>Want a callback?</Eyebrow>
                <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight">Let's talk about your care.</h2>
                <p className="mt-4 text-base sm:text-lg leading-relaxed text-[#525252]">Leave your details and a Raphadon specialist will reach out to guide your next step.</p>
              </div>
              <div className="mt-8"><CallbackForm /></div>
            </div>
          </Reveal>
        </Container>
      </section>
    </div>
  );
}
